import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../apiClient';
import './Support.css';

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, resolved

  useEffect(() => {
    fetchTickets();
  }, [filter]);

  const fetchTickets = async () => {
    try {
      let url = '/api/support/tickets';
      if (filter !== 'all') {
        url += `?status=${filter}`;
      }
      
      const response = await apiClient.get(url);
      if (response.data.success) {
        setTickets(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Kunde inte hämta tickets');
    } finally {
      setLoading(false);
    }
  };

  const resolveTicket = async (ticketId) => {
    try {
      const response = await apiClient.put(`/api/support/ticket/${ticketId}/resolve`);
      if (response.data.success) {
        toast.success('Ticket markerad som löst');
        fetchTickets(); // Refresh list
      }
    } catch (error) {
      console.error('Error resolving ticket:', error);
      toast.error('Kunde inte uppdatera ticket');
    }
  };

  const approveMedicalTerm = async (ticketId) => {
    try {
      const response = await apiClient.post(`/api/support/ticket/${ticketId}/approve-medical-term`, {}, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        toast.success('Medicinsk term godkänd och tillagd!');
        fetchTickets(); // Refresh list
      }
    } catch (error) {
      console.error('Error approving medical term:', error);
      toast.error('Kunde inte godkänna medicinsk term: ' + (error.response?.data?.error || 'Okänt fel'));
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'teknisk support': 'Teknisk support',
      'funktionsfråga': 'Funktionsfråga',
      'bugg': 'Bugg',
      'lägg till medicinsk term': 'Medicinsk term',
      'annat': 'Annat'
    };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE') + ' ' + date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const getMedicalCategoryLabel = (category) => {
    const labels = {
      'global': 'Global',
      'medication': 'Medicin/Läkemedel',
      'diagnosis': 'Diagnos',
      'test': 'Test/Undersökning',
      'anatomy': 'Anatomi',
      'other': 'Övrigt'
    };
    return labels[category] || category;
  };

  const getProfessionLabel = (profession) => {
    const labels = {
      'alla': 'Alla',
      'doctor': 'Läkare',
      'physiotherapist': 'Sjukgymnast',
      'psychologist': 'Psykolog',
      'nurse': 'Sköterska',
      'other': 'Övrigt'
    };
    return labels[profession] || profession;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Laddar...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Support Tickets</h2>
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('all')}
          >
            Alla ({tickets.length})
          </button>
          <button
            type="button"
            className={`btn ${filter === 'open' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('open')}
          >
            Öppna
          </button>
          <button
            type="button"
            className={`btn ${filter === 'resolved' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('resolved')}
          >
            Lösta
          </button>
        </div>
      </div>

      {tickets.length === 0 ? (
        <div className="alert alert-info">
          Inga tickets att visa.
        </div>
      ) : (
        <div className="ticket-list">
          {tickets.map((ticket) => (
            <div key={ticket.id} className={`ticket-card ${ticket.status === 'resolved' ? 'ticket-resolved' : ''}`}>
              <div className="ticket-header">
                <div>
                  <h5 className="mb-1">
                    #{ticket.id} - {ticket.subject}
                  </h5>
                  <div className="ticket-meta">
                    <span className="badge badge-secondary mr-2">
                      {getCategoryLabel(ticket.category)}
                    </span>
                    <span className="text-muted">
                      {ticket.user_name} - {ticket.clinic_name}
                    </span>
                  </div>
                </div>
                <div className="ticket-status">
                  {ticket.status === 'open' ? (
                    <div className="d-flex gap-2">
                      {ticket.category === 'lägg till medicinsk term' ? (
                        <>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => approveMedicalTerm(ticket.id)}
                            style={{
                              backgroundColor: '#007bff',
                              border: '2px solid #007bff',
                              color: '#ffffff',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              marginRight: '8px'
                            }}
                          >
                            Godkänn term
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => resolveTicket(ticket.id)}
                            style={{
                              backgroundColor: '#6c757d',
                              border: '2px solid #6c757d',
                              color: '#ffffff',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          >
                            Avslå
                          </button>
                        </>
                      ) : (
                        <button
                          className="ticket-resolve-button"
                          onClick={() => resolveTicket(ticket.id)}
                          style={{
                            backgroundColor: '#28a745',
                            border: '2px solid #28a745',
                            color: '#ffffff',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            minWidth: '140px',
                            minHeight: '32px'
                          }}
                        >
                          Markera som löst
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="badge badge-success ticket-resolved-badge">Löst</span>
                  )}
                </div>
              </div>
              
              <div className="ticket-body">
                {/* Visa beskrivning endast för icke-medicinska term-tickets */}
                {ticket.category !== 'lägg till medicinsk term' && ticket.description && (
                  <p className="mb-2">{ticket.description}</p>
                )}
                
                {/* Visa medicinska term-data om det är en medicinsk term-ticket */}
                {ticket.category === 'lägg till medicinsk term' && (
                  <div className="medical-term-details mb-3">
                    <h6 className="mb-2"><strong>Medicinsk term-information:</strong></h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Felstavad term:</strong> <span className="text-danger">{ticket.incorrect_term}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Korrekt stavning:</strong> <span className="text-success">{ticket.correct_term}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Typ:</strong> {getMedicalCategoryLabel(ticket.medical_category)}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Profession:</strong> {getProfessionLabel(ticket.profession)}
                        </div>
                        {ticket.context && (
                          <div className="mb-2">
                            <strong>Kontext:</strong> {ticket.context}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="ticket-footer">
                  <small className="text-muted">
                    <i className="far fa-clock mr-1"></i>
                    Skapad: {formatDate(ticket.created_at)}
                  </small>
                  {ticket.resolved_at && (
                    <small className="text-muted ml-3">
                      <i className="fas fa-check mr-1"></i>
                      Löst: {formatDate(ticket.resolved_at)}
                    </small>
                  )}
                  <small className="text-muted ml-3">
                    <i className="far fa-envelope mr-1"></i>
                    {ticket.user_email}
                  </small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketList;